// hotel/F&B/StaffDailyReport.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Receipt,
  Print,
  PictureAsPdf,
  Description,
  Refresh,
  Search,
  Clear,
  AttachMoney,
  CreditCard,
  Payments,
  School,
  EventNote,
  Today,
  TrendingUp,
  CheckCircle,
  Cancel,
  LocalBar,
  RoomService,
  TakeoutDining,
  DeliveryDining,
  Restaurant,
  Fastfood,
  ShoppingBag,
  ExpandMore,
  QrCodeScanner
} from '@mui/icons-material';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';

import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Sidebar from '../../../../components/sidebars/sidebar_f&b';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// Order Types Mapping
const ORDER_TYPES = {
  dine_in: { label: 'Dine In', icon: <Restaurant />, color: '#1976D2' },
  Lounge: { label: 'Lounge/Arena', icon: <LocalBar />, color: '#9C27B0' },
  room_service: { label: 'Room Service', icon: <RoomService />, color: '#2E7D32' },
  takeaway: { label: 'Takeaway', icon: <TakeoutDining />, color: '#ED6C02' },
  delivery: { label: 'Delivery', icon: <DeliveryDining />, color: '#0288D1' }
};

// Payment Methods
const PAYMENT_METHODS = {
  Cash: { label: 'Cash', icon: <AttachMoney />, color: '#4CAF50' },
  'POS Terminal': { label: 'POS', icon: <CreditCard />, color: '#2196F3' },
  'Bank Transfer': { label: 'Transfer', icon: <Payments />, color: '#9C27B0' },
  Complimentary: { label: 'Complimentary', icon: <School />, color: '#9E9E9E' }
};

const StatsCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6]
  }
}));

const ReportHeader = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
  color: 'white',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden'
}));

// Helper Functions
const getCurrentStaffId = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || storedProfile.staffId || null;
  } catch (error) {
    console.error('Error getting staff ID:', error);
    return null;
  }
};

const getCurrentStaffName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return `${storedProfile.first_name || ''} ${storedProfile.other_names || ''}`.trim() || storedProfile.name || 'Staff';
  } catch (error) {
    console.error('Error getting staff name:', error);
    return 'Staff';
  }
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A';
  try {
    const date = new Date(dateTimeStr);
    return format(date, 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return dateTimeStr;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A';
  try {
    const date = new Date(dateTimeStr);
    return format(date, 'HH:mm:ss');
  } catch {
    return dateTimeStr;
  }
};

/**
 * API Service
 */
const apiService = {
  getStaffDailyReport: async (staffId, hotelCode, date, format = 'json') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/receipt.php`, {
      params: {
        staff_id: staffId,
        hotel_code: hotelCode,
        date: date,
        format: format
      }
    });
    return response.data;
  }
};

/**
 * Main StaffDailyReport Component
 */
const StaffDailyReport = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedOrders, setExpandedOrders] = useState({});

  // Data States
  const [reportData, setReportData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [staff, setStaff] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [shift, setShift] = useState(null);

  // Filter States
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog States
  const [openReportPreview, setOpenReportPreview] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data on mount and when date changes
  useEffect(() => {
    loadReport();
  }, [selectedDate]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await apiService.getStaffDailyReport(
        getCurrentStaffId(), 
        getHotelCode(), 
        selectedDate,
        'json'
      );
      
      if (data.success) {
        setReportData(data);
        setPayments(data.payments || []);
        setStatistics(data.statistics);
        setStaff(data.staff);
        setHotel(data.hotel);
        setShift(data.shift);
      } else {
        showSnackbar(data.error || 'Failed to load report', 'error');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      showSnackbar('Failed to load report: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadReport();
      showSnackbar('Report refreshed successfully', 'success');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  /**
   * Enhanced Thermal Printer Report with Item Details and Quantities
   */
  const printThermalReport = async () => {
    try {
      const staffName = staff ? `${staff.first_name || ''} ${staff.other_names || ''}`.trim() : getCurrentStaffName();
      const hotelName = hotel?.hotel_name || 'Frankville Hotels';
      const hotelAddress = hotel?.hotel_address || '';
      const hotelPhone = hotel?.hotel_phone || '';
      const reportDate = format(new Date(selectedDate), 'dd/MM/yyyy');
      const stats = statistics || {};
      const filteredPayments = payments;
      
      // Separate complimentary and paid transactions
      const paidPayments = filteredPayments.filter(p => p.payment_method !== 'Complimentary');
      const complimentaryPayments = filteredPayments.filter(p => p.payment_method === 'Complimentary');
      
      // Helper function to render items with quantities
      const renderItems = (items) => {
        if (!items || items.length === 0) return '';
        
        let itemsHtml = '<div style="margin-top: 6px; margin-left: 4px;">';
        items.forEach(item => {
          const quantity = item.quantity || item.qty || 1;
          const itemName = item.item_name || item.name || item.product_name || 'Item';
          const itemPrice = item.price || item.unit_price || 0;
          const itemTotal = quantity * itemPrice;
          
          itemsHtml += `
            <div style="display: flex; justify-content: space-between; font-size: 8px; margin-bottom: 2px; padding-left: 4px;">
              <span style="font-weight: 600;">${quantity}x ${itemName}</span>
              <span style="font-weight: 500;">${formatCurrency(itemTotal)}</span>
            </div>
            ${item.modifiers && item.modifiers.length > 0 ? `
              <div style="font-size: 7px; color: #555; margin-left: 12px; margin-bottom: 2px;">
                ${item.modifiers.map(m => `+ ${m.name || m}`).join(', ')}
              </div>
            ` : ''}
          `;
        });
        itemsHtml += '</div>';
        return itemsHtml;
      };
      
      // Create enhanced HTML for thermal printing with item details
      const thermalHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Staff Daily Report - ${hotelName}</title>
          <style>
            @page {
              size: 80mm auto;
              margin: 0mm;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Courier New', 'Lucida Console', monospace;
              font-size: 10px;
              line-height: 1.3;
              color: #000000;
              background: white;
              padding: 3mm;
              width: 80mm;
              margin: 0 auto;
            }
            
            .thermal-container {
              width: 100%;
              max-width: 74mm;
              margin: 0 auto;
            }
            
            .header-main {
              text-align: center;
              font-weight: 900;
              font-size: 16px;
              letter-spacing: 1px;
              margin-bottom: 4px;
              color: #000000;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .header-sub {
              text-align: center;
              font-weight: 800;
              font-size: 12px;
              margin-bottom: 4px;
              color: #000000;
            }
            
            .header-details {
              text-align: center;
              font-size: 9px;
              margin-bottom: 2px;
              color: #000000;
            }
            
            .section-title {
              font-weight: 900;
              font-size: 11px;
              margin-top: 8px;
              margin-bottom: 4px;
              padding-bottom: 2px;
              border-bottom: 2px solid #000000;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #000000;
            }
            
            .divider {
              border-top: 1px dashed #000000;
              margin: 6px 0;
            }
            
            .divider-solid {
              border-top: 2px solid #000000;
              margin: 6px 0;
            }
            
            .row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 3px;
              font-size: 9px;
            }
            
            .row-label {
              font-weight: 800;
              color: #000000;
            }
            
            .row-value {
              font-weight: 600;
              color: #000000;
            }
            
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 6px;
              margin: 8px 0;
            }
            
            .stat-card {
              border: 1.5px solid #000000;
              padding: 6px;
              text-align: center;
              background: #f8f8f8;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .stat-label {
              font-size: 8px;
              font-weight: 600;
              color: #000000;
              margin-bottom: 2px;
            }
            
            .stat-value {
              font-size: 13px;
              font-weight: 900;
              color: #000000;
            }
            
            .transaction-item {
              margin-bottom: 12px;
              padding: 6px;
              border: 1.5px solid #000000;
              background: #ffffff;
              page-break-inside: avoid;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .transaction-header {
              font-weight: 900;
              font-size: 10px;
              margin-bottom: 6px;
              padding-bottom: 3px;
              border-bottom: 1.5px solid #000000;
              display: flex;
              justify-content: space-between;
            }
            
            .transaction-detail {
              font-size: 8px;
              margin-bottom: 3px;
              display: flex;
              justify-content: space-between;
            }
            
            .items-list {
              margin-top: 6px;
              margin-left: 4px;
              border-top: 1px dotted #000000;
              padding-top: 4px;
            }
            
            .item-row {
              display: flex;
              justify-content: space-between;
              font-size: 8px;
              margin-bottom: 2px;
              padding-left: 4px;
            }
            
            .item-name {
              font-weight: 600;
            }
            
            .item-price {
              font-weight: 500;
            }
            
            .complimentary-badge {
              font-weight: 900;
              color: #000000;
              background: #e0e0e0;
              padding: 2px 4px;
              font-size: 7px;
              display: inline-block;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .amount-paid {
              font-weight: 900;
              font-size: 11px;
              color: #000000;
            }
            
            .footer {
              text-align: center;
              margin-top: 12px;
              padding-top: 6px;
              border-top: 2px solid #000000;
              font-size: 7px;
              color: #000000;
            }
            
            .payment-method {
              display: inline-block;
              padding: 2px 6px;
              background: #000000;
              color: #ffffff;
              font-weight: 900;
              font-size: 7px;
              margin: 2px 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 2mm;
              }
              .no-break {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="thermal-container">
            <!-- Header Section -->
            <div style="text-align: center; margin-bottom: 10px;">
              <div class="header-main">${hotelName}</div>
              ${hotelAddress ? `<div class="header-details">${hotelAddress}</div>` : ''}
              ${hotelPhone ? `<div class="header-details">Tel: ${hotelPhone}</div>` : ''}
              <div class="header-sub">STAFF DAILY SALES REPORT</div>
              <div class="divider-solid"></div>
              <div class="row">
                <span class="row-label">Staff:</span>
                <span class="row-value">${staffName}</span>
              </div>
              <div class="row">
                <span class="row-label">ID:</span>
                <span class="row-value">${staff?.staff_id || getCurrentStaffId() || 'N/A'}</span>
              </div>
              <div class="row">
                <span class="row-label">Date:</span>
                <span class="row-value">${reportDate}</span>
              </div>
              ${shift ? `
              <div class="row">
                <span class="row-label">Shift:</span>
                <span class="row-value">${shift.shift_start_time || '08:00'} - ${shift.shift_end_time || '07:30'}</span>
              </div>
              ` : ''}
              <div class="row">
                <span class="row-label">Print Time:</span>
                <span class="row-value">${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</span>
              </div>
              <div class="divider-solid"></div>
            </div>
            
            <!-- Statistics Section -->
            <div class="section-title">PERFORMANCE SUMMARY</div>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">💰 TOTAL REVENUE</div>
                <div class="stat-value">${formatCurrency(stats.total_revenue || 0)}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">📋 TOTAL ORDERS</div>
                <div class="stat-value">${stats.total_orders || 0}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">📊 AVG ORDER</div>
                <div class="stat-value">${formatCurrency(stats.average_order_value || 0)}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">🎁 COMPLIMENTARY</div>
                <div class="stat-value">${stats.complimentary_count || 0}</div>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <!-- Payment Breakdown -->
            <div class="section-title">PAYMENT BREAKDOWN</div>
            ${(stats.cash_total || 0) > 0 ? `
            <div class="row">
              <span class="row-label">Cash:</span>
              <span class="row-value">${formatCurrency(stats.cash_total || 0)}</span>
            </div>
            ` : ''}
            ${(stats.pos_total || 0) > 0 ? `
            <div class="row">
              <span class="row-label">POS Terminal:</span>
              <span class="row-value">${formatCurrency(stats.pos_total || 0)}</span>
            </div>
            ` : ''}
            ${(stats.transfer_total || 0) > 0 ? `
            <div class="row">
              <span class="row-label">Bank Transfer:</span>
              <span class="row-value">${formatCurrency(stats.transfer_total || 0)}</span>
            </div>
            ` : ''}
            ${(stats.complimentary_amount || 0) > 0 ? `
            <div class="row">
              <span class="row-label">Complimentary:</span>
              <span class="row-value">${stats.complimentary_count || 0} orders (${formatCurrency(stats.complimentary_amount || 0)})</span>
            </div>
            ` : ''}
            
            <div class="divider-solid"></div>
            
            <!-- Order Type Breakdown -->
            ${statistics?.order_type_breakdown && Object.keys(statistics.order_type_breakdown).length > 0 ? `
            <div class="section-title">ORDER TYPES</div>
            ${Object.entries(statistics.order_type_breakdown).map(([type, data]) => `
              <div class="row">
                <span class="row-label">${ORDER_TYPES[type]?.label || type}:</span>
                <span class="row-value">${data.count} orders (${formatCurrency(data.revenue)})</span>
              </div>
            `).join('')}
            <div class="divider-solid"></div>
            ` : ''}
            
            <!-- Paid Transactions with Items -->
            ${paidPayments.length > 0 ? `
            <div class="section-title">✅ PAID TRANSACTIONS (${paidPayments.length})</div>
            ${paidPayments.map((payment, index) => `
              <div class="transaction-item no-break">
                <div class="transaction-header">
                  <span>#${payment.order_number || 'N/A'}</span>
                  <span class="amount-paid">${formatCurrency(payment.paid_amount)}</span>
                </div>
                <div class="transaction-detail">
                  <span>Type: ${ORDER_TYPES[payment.order_type]?.label || payment.order_type || 'N/A'}</span>
                  <span>Method: ${payment.payment_method || 'N/A'}</span>
                </div>
                <div class="transaction-detail">
                  <span>${payment.table_number ? `Table ${payment.table_number}` : (payment.room_number ? `Room ${payment.room_number}` : 'Walk-in')}</span>
                  <span>Items: ${payment.items?.length || 0}</span>
                </div>
                <div class="transaction-detail">
                  <span>${payment.guest_name === 'Walk-in Customer' ? 'Walk-in' : (payment.guest_name || 'Guest')}</span>
                  <span>${formatTime(payment.payment_date)}</span>
                </div>
                ${payment.items && payment.items.length > 0 ? `
                  <div class="items-list">
                    ${payment.items.map(item => {
                      const quantity = item.quantity || item.qty || 1;
                      const itemName = item.item_name || item.name || item.product_name || 'Item';
                      const itemPrice = item.price || item.unit_price || 0;
                      const itemTotal = quantity * itemPrice;
                      return `
                        <div class="item-row">
                          <span class="item-name">${quantity}x ${itemName}</span>
                          <span class="item-price">${formatCurrency(itemTotal)}</span>
                        </div>
                        ${item.modifiers && item.modifiers.length > 0 ? `
                          <div style="font-size: 7px; color: #555; margin-left: 12px; margin-bottom: 2px;">
                            ${item.modifiers.map(m => `+ ${m.name || m}`).join(', ')}
                          </div>
                        ` : ''}
                      `;
                    }).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
            <div class="divider"></div>
            ` : ''}
            
            <!-- Complimentary Transactions with Items -->
            ${complimentaryPayments.length > 0 ? `
            <div class="section-title">🎁 COMPLIMENTARY (${complimentaryPayments.length})</div>
            ${complimentaryPayments.map((payment, index) => `
              <div class="transaction-item no-break">
                <div class="transaction-header">
                  <span>#${payment.order_number || 'N/A'}</span>
                  <span class="complimentary-badge">COMPLIMENTARY</span>
                </div>
                <div class="transaction-detail">
                  <span>Type: ${ORDER_TYPES[payment.order_type]?.label || payment.order_type || 'N/A'}</span>
                  <span>Guest: ${payment.guest_name === 'Walk-in Customer' ? 'Walk-in' : (payment.guest_name || 'Guest')}</span>
                </div>
                <div class="transaction-detail">
                  <span>${payment.table_number ? `Table ${payment.table_number}` : (payment.room_number ? `Room ${payment.room_number}` : 'Walk-in')}</span>
                  <span>Items: ${payment.items?.length || 0}</span>
                </div>
                <div class="transaction-detail">
                  <span>Time: ${formatTime(payment.payment_date)}</span>
                </div>
                ${payment.items && payment.items.length > 0 ? `
                  <div class="items-list">
                    ${payment.items.map(item => {
                      const quantity = item.quantity || item.qty || 1;
                      const itemName = item.item_name || item.name || item.product_name || 'Item';
                      return `
                        <div class="item-row">
                          <span class="item-name">${quantity}x ${itemName}</span>
                        </div>
                      `;
                    }).join('')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
            <div class="divider"></div>
            ` : ''}
            
            ${filteredPayments.length === 0 ? `
            <div style="text-align: center; padding: 20px 0;">
              <div class="row-label">No transactions found for this date</div>
            </div>
            ` : ''}
            
            <!-- Total Items Summary -->
            <div class="section-title">ITEMS SUMMARY</div>
            <div class="row">
              <span class="row-label">Total Items Sold:</span>
              <span class="row-value">${payments.reduce((sum, p) => sum + (p.items?.reduce((itemSum, item) => itemSum + (item.quantity || item.qty || 1), 0) || 0), 0)}</span>
            </div>
            <div class="row">
              <span class="row-label">Average Items/Order:</span>
              <span class="row-value">${(payments.reduce((sum, p) => sum + (p.items?.length || 0), 0) / (payments.length || 1)).toFixed(1)}</span>
            </div>
            
            <!-- Footer -->
            <div class="footer">
              <div>${hotelName} F&B System</div>
              <div>Report generated by ${staffName}</div>
              <div>Thank you for your service! 👏</div>
              <div style="margin-top: 4px;">${'-'.repeat(32)}</div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 1000);
            }
          </script>
        </body>
        </html>
      `;
      
      // Create print window with enhanced thermal styles
      const printWindow = window.open('', '_blank', 'width=450,height=650,toolbar=yes,scrollbars=yes');
      if (printWindow) {
        printWindow.document.write(thermalHTML);
        printWindow.document.close();
        showSnackbar('Thermal report printed with item details', 'success');
      } else {
        showSnackbar('Please allow pop-ups to print the report', 'error');
      }
    } catch (error) {
      console.error('Error printing thermal report:', error);
      showSnackbar('Failed to print report: ' + error.message, 'error');
    }
  };

  // Enhanced PDF Export with Item Details
  const exportToPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const staffName = staff ? `${staff.first_name || ''} ${staff.other_names || ''}`.trim() : getCurrentStaffName();
    const hotelName = hotel?.hotel_name || 'Frankville Hotels';
    const reportDate = format(new Date(selectedDate), 'dd/MM/yyyy');
    const stats = statistics || {};
    const filteredPayments = payments;
    
    // Separate complimentary and paid transactions
    const paidPayments = filteredPayments.filter(p => p.payment_method !== 'Complimentary');
    const complimentaryPayments = filteredPayments.filter(p => p.payment_method === 'Complimentary');
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;
    
    // Header
    doc.setFillColor(26, 35, 126);
    doc.rect(0, 0, pageWidth, 45, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(hotelName, pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Staff Daily Sales Report', pageWidth / 2, 30, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, pageWidth / 2, 40, { align: 'center' });
    
    yPos = 55;
    
    // Staff Info Card
    doc.setFillColor(240, 242, 245);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 35, 'F');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('STAFF INFORMATION', margin + 5, yPos + 8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Staff: ${staffName}`, margin + 10, yPos + 18);
    doc.text(`ID: ${staff?.staff_id || getCurrentStaffId() || 'N/A'}`, margin + 10, yPos + 26);
    doc.text(`Date: ${reportDate}`, margin + 10, yPos + 34);
    yPos += 45;
    
    // Statistics Cards
    const summaryCards = [
      { label: '💰 Total Revenue', value: formatCurrency(stats.total_revenue || 0), color: '#2E7D32' },
      { label: '📋 Total Orders', value: (stats.total_orders || 0).toString(), color: '#1976D2' },
      { label: '📊 Avg Order Value', value: formatCurrency(stats.average_order_value || 0), color: '#FF9800' },
      { label: '🎁 Complimentary', value: (stats.complimentary_count || 0).toString(), color: '#9E9E9E' }
    ];
    
    const cardWidth = (pageWidth - (margin * 2) - 12) / 4;
    summaryCards.forEach((card, idx) => {
      const x = margin + (idx * (cardWidth + 4));
      doc.setFillColor(250, 250, 250);
      doc.rect(x, yPos, cardWidth, 25, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.rect(x, yPos, cardWidth, 25, 'D');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(card.label, x + 3, yPos + 7);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(card.value, x + 3, yPos + 20);
    });
    yPos += 35;
    
    // Payment Breakdown Section
    doc.setFillColor(66, 66, 66);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYMENT BREAKDOWN', margin + 5, yPos + 6);
    yPos += 15;
    
    const paymentMethods = [
      { label: 'Cash', amount: stats.cash_total || 0, color: '#4CAF50' },
      { label: 'POS Terminal', amount: stats.pos_total || 0, color: '#2196F3' },
      { label: 'Bank Transfer', amount: stats.transfer_total || 0, color: '#9C27B0' },
      { label: 'Complimentary', amount: stats.complimentary_amount || 0, count: stats.complimentary_count || 0, color: '#9E9E9E' }
    ];
    
    paymentMethods.forEach((method, idx) => {
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos + (idx * 10), pageWidth - (margin * 2), 9, 'F');
      
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      
      const displayValue = method.count !== undefined 
        ? `${method.count} order(s) (${formatCurrency(method.amount)})` 
        : formatCurrency(method.amount);
      
      doc.text(`${method.label}: ${displayValue}`, margin + 5, yPos + (idx * 10) + 6);
    });
    yPos += paymentMethods.length * 10 + 10;
    
    // Order Type Breakdown
    if (stats.order_type_breakdown && Object.keys(stats.order_type_breakdown).length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = margin;
      }
      
      doc.setFillColor(66, 66, 66);
      doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('ORDER TYPE BREAKDOWN', margin + 5, yPos + 6);
      yPos += 15;
      
      Object.entries(stats.order_type_breakdown).forEach(([type, data], idx) => {
        const typeInfo = ORDER_TYPES[type] || { label: type };
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos + (idx * 10), pageWidth - (margin * 2), 9, 'F');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`${typeInfo.label}: ${data.count} orders (${formatCurrency(data.revenue)})`, margin + 5, yPos + (idx * 10) + 6);
      });
      yPos += Object.keys(stats.order_type_breakdown).length * 10 + 10;
    }
    
    // Paid Transactions Section with Item Details
    if (paidPayments.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = margin;
      }
      
      doc.setFillColor(33, 150, 243);
      doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('PAID TRANSACTIONS', margin + 5, yPos + 6);
      yPos += 15;
      
      for (let i = 0; i < paidPayments.length; i++) {
        const payment = paidPayments[i];
        const orderType = ORDER_TYPES[payment.order_type]?.label || payment.order_type || 'N/A';
        const tableInfo = payment.table_number ? `Table ${payment.table_number}` : (payment.room_number ? `Room ${payment.room_number}` : 'Walk-in');
        const guestName = payment.guest_name === 'Walk-in Customer' ? 'Walk-in' : (payment.guest_name || 'Guest');
        
        // Check if we need a new page
        const itemsHeight = (payment.items?.length || 0) * 8;
        const cardHeight = 35 + itemsHeight;
        
        if (yPos + cardHeight > 270) {
          doc.addPage();
          yPos = margin;
          doc.setFillColor(33, 150, 243);
          doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.text('PAID TRANSACTIONS (Continued)', margin + 5, yPos + 6);
          yPos += 15;
        }
        
        // Transaction card background
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 28 + (payment.items?.length || 0) * 8, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 28 + (payment.items?.length || 0) * 8, 'D');
        
        // Transaction header
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`Order: ${payment.order_number || 'N/A'}`, margin + 5, yPos + 6);
        doc.setFont('helvetica', 'normal');
        doc.text(`Amount: ${formatCurrency(payment.paid_amount)}`, pageWidth - margin - 45, yPos + 6);
        
        // Transaction details
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        doc.text(`Type: ${orderType} | Location: ${tableInfo}`, margin + 5, yPos + 14);
        doc.text(`Guest: ${guestName}`, margin + 5, yPos + 20);
        doc.text(`Time: ${formatTime(payment.payment_date)} | Method: ${payment.payment_method}`, margin + 5, yPos + 26);
        
        // Items list with quantities
        let itemsY = yPos + 30;
        if (payment.items && payment.items.length > 0) {
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');
          doc.text('Items:', margin + 5, itemsY);
          itemsY += 4;
          
          payment.items.forEach((item, idx) => {
            const quantity = item.quantity || item.qty || 1;
            const itemName = item.item_name || item.name || item.product_name || 'Item';
            const itemPrice = item.price || item.unit_price || 0;
            const itemTotal = quantity * itemPrice;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.text(`${quantity}x ${itemName}`, margin + 8, itemsY);
            doc.text(formatCurrency(itemTotal), pageWidth - margin - 20, itemsY);
            itemsY += 5;
            
            // Modifiers if any
            if (item.modifiers && item.modifiers.length > 0) {
              doc.setFontSize(7);
              doc.setTextColor(100, 100, 100);
              item.modifiers.forEach(mod => {
                doc.text(`  + ${mod.name || mod}`, margin + 12, itemsY);
                itemsY += 4;
              });
              doc.setTextColor(80, 80, 80);
            }
          });
        }
        
        yPos += 35 + (payment.items?.length || 0) * 8 + 5;
      }
    }
    
    // Complimentary Transactions Section with Item Details
    if (complimentaryPayments.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = margin;
      }
      
      doc.setFillColor(158, 158, 158);
      doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPLIMENTARY TRANSACTIONS', margin + 5, yPos + 6);
      yPos += 15;
      
      for (let i = 0; i < complimentaryPayments.length; i++) {
        const payment = complimentaryPayments[i];
        const orderType = ORDER_TYPES[payment.order_type]?.label || payment.order_type || 'N/A';
        const tableInfo = payment.table_number ? `Table ${payment.table_number}` : (payment.room_number ? `Room ${payment.room_number}` : 'Walk-in');
        const guestName = payment.guest_name === 'Walk-in Customer' ? 'Walk-in' : (payment.guest_name || 'Guest');
        
        // Check if we need a new page
        const itemsHeight = (payment.items?.length || 0) * 6;
        const cardHeight = 28 + itemsHeight;
        
        if (yPos + cardHeight > 270) {
          doc.addPage();
          yPos = margin;
          doc.setFillColor(158, 158, 158);
          doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.text('COMPLIMENTARY TRANSACTIONS (Continued)', margin + 5, yPos + 6);
          yPos += 15;
        }
        
        // Transaction card background
        doc.setFillColor(250, 250, 245);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 22 + (payment.items?.length || 0) * 6, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 22 + (payment.items?.length || 0) * 6, 'D');
        
        // Transaction header
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'bold');
        doc.text(`Order: ${payment.order_number || 'N/A'}`, margin + 5, yPos + 6);
        doc.setFont('helvetica', 'italic');
        doc.text('COMPLIMENTARY', pageWidth - margin - 45, yPos + 6);
        
        // Transaction details
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        doc.text(`Type: ${orderType} | Location: ${tableInfo}`, margin + 5, yPos + 14);
        doc.text(`Guest: ${guestName}`, margin + 5, yPos + 20);
        
        // Items list
        let itemsY = yPos + 24;
        if (payment.items && payment.items.length > 0) {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.text('Items:', margin + 5, itemsY);
          itemsY += 3;
          
          payment.items.forEach((item) => {
            const quantity = item.quantity || item.qty || 1;
            const itemName = item.item_name || item.name || item.product_name || 'Item';
            doc.setFont('helvetica', 'normal');
            doc.text(`${quantity}x ${itemName}`, margin + 8, itemsY);
            itemsY += 4;
          });
        }
        
        yPos += 25 + (payment.items?.length || 0) * 6 + 5;
      }
    }
    
    if (filteredPayments.length === 0) {
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('No transactions found for the selected date', pageWidth / 2, yPos + 10, { align: 'center' });
    }
    
    // Items Summary Section
    const totalItemsSold = payments.reduce((sum, p) => sum + (p.items?.reduce((itemSum, item) => itemSum + (item.quantity || item.qty || 1), 0) || 0), 0);
    const totalOrderItems = payments.reduce((sum, p) => sum + (p.items?.length || 0), 0);
    const avgItemsPerOrder = payments.length > 0 ? (totalOrderItems / payments.length).toFixed(1) : 0;
    
    if (yPos > 250) {
      doc.addPage();
      yPos = margin;
    }
    
    doc.setFillColor(66, 66, 66);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMS SUMMARY', margin + 5, yPos + 6);
    yPos += 15;
    
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 20, 'F');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Items Sold: ${totalItemsSold}`, margin + 5, yPos + 8);
    doc.text(`Average Items/Order: ${avgItemsPerOrder}`, margin + 5, yPos + 16);
    yPos += 25;
    
    // Footer on all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `${hotelName} F&B System - Page ${i} of ${totalPages} - Generated by ${staffName}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    const fileName = `staff_daily_report_${staff?.staff_id || getCurrentStaffId()}_${format(new Date(selectedDate), 'yyyyMMdd')}.pdf`;
    doc.save(fileName);
    showSnackbar('PDF report downloaded successfully', 'success');
  };
  
  // Excel Export with Item Details
  const exportToExcel = () => {
    const rows = [];
    
    // Create a row for each item in each payment
    payments.forEach(payment => {
      const orderType = ORDER_TYPES[payment.order_type]?.label || payment.order_type || 'N/A';
      const tableInfo = payment.table_number ? `Table ${payment.table_number}` : (payment.room_number ? `Room ${payment.room_number}` : 'Walk-in');
      
      if (payment.items && payment.items.length > 0) {
        payment.items.forEach(item => {
          const quantity = item.quantity || item.qty || 1;
          const itemName = item.item_name || item.name || item.product_name || 'Item';
          const itemPrice = item.price || item.unit_price || 0;
          const itemTotal = quantity * itemPrice;
          
          rows.push({
            'Order Number': payment.order_number || 'N/A',
            'Payment Number': payment.payment_number || 'N/A',
            'Order Type': orderType,
            'Table/Room': tableInfo,
            'Guest Name': payment.guest_name || 'Guest',
            'Phone': payment.guest_phone || 'N/A',
            'Item Name': itemName,
            'Quantity': quantity,
            'Unit Price (₦)': parseFloat(itemPrice || 0).toFixed(2),
            'Item Total (₦)': parseFloat(itemTotal || 0).toFixed(2),
            'Modifiers': item.modifiers ? item.modifiers.map(m => m.name || m).join(', ') : '',
            'Payment Method': payment.payment_method || 'N/A',
            'Payment Time': formatTime(payment.payment_date),
            'Payment Date': formatDate(payment.payment_date),
            'Payment Status': payment.payment_status || 'completed',
            'Recorded By': payment.payment_created_by_name || 'Staff'
          });
        });
      } else {
        // Fallback if no items data
        rows.push({
          'Order Number': payment.order_number || 'N/A',
          'Payment Number': payment.payment_number || 'N/A',
          'Order Type': orderType,
          'Table/Room': tableInfo,
          'Guest Name': payment.guest_name || 'Guest',
          'Phone': payment.guest_phone || 'N/A',
          'Item Name': 'N/A',
          'Quantity': 0,
          'Unit Price (₦)': '0.00',
          'Item Total (₦)': parseFloat(payment.paid_amount || 0).toFixed(2),
          'Modifiers': '',
          'Payment Method': payment.payment_method || 'N/A',
          'Payment Time': formatTime(payment.payment_date),
          'Payment Date': formatDate(payment.payment_date),
          'Payment Status': payment.payment_status || 'completed',
          'Recorded By': payment.payment_created_by_name || 'Staff'
        });
      }
    });
    
    // Add summary row
    const totalItemsSold = payments.reduce((sum, p) => sum + (p.items?.reduce((itemSum, item) => itemSum + (item.quantity || item.qty || 1), 0) || 0), 0);
    
    rows.push({
      'Order Number': '=== SUMMARY ===',
      'Payment Number': '',
      'Order Type': '',
      'Table/Room': '',
      'Guest Name': '',
      'Phone': '',
      'Item Name': `TOTAL ITEMS: ${totalItemsSold}`,
      'Quantity': '',
      'Unit Price (₦)': '',
      'Item Total (₦)': (statistics?.total_revenue || 0).toFixed(2),
      'Modifiers': '',
      'Payment Method': '',
      'Payment Time': '',
      'Payment Date': '',
      'Payment Status': '',
      'Recorded By': ''
    });
    
    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row => headers.map(header => {
        const value = row[header]?.toString() || '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `staff_daily_report_${staff?.staff_id || getCurrentStaffId()}_${format(new Date(selectedDate), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSnackbar('Excel report exported successfully with item details', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  const handleLogout = () => {
    navigate('/HotelAdmin');
  };

  const handleNotificationClick = () => {};

  // Date navigation
  const goToPreviousDay = () => {
    const prevDate = subDays(new Date(selectedDate), 1);
    setSelectedDate(format(prevDate, 'yyyy-MM-dd'));
  };

  const goToNextDay = () => {
    const nextDate = subDays(new Date(selectedDate), -1);
    if (nextDate <= new Date()) {
      setSelectedDate(format(nextDate, 'yyyy-MM-dd'));
    }
  };

  const goToToday = () => {
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const getFilteredPayments = () => {
    if (!searchQuery) return payments;
    const query = searchQuery.toLowerCase();
    return payments.filter(payment => 
      payment.order_number?.toLowerCase().includes(query) ||
      payment.payment_number?.toLowerCase().includes(query) ||
      payment.guest_name?.toLowerCase().includes(query)
    );
  };

  const getPaymentMethodChip = (method) => {
    const methodInfo = PAYMENT_METHODS[method];
    return methodInfo ? (
      <Chip 
        icon={methodInfo.icon} 
        label={methodInfo.label} 
        size="small" 
        sx={{ bgcolor: `${methodInfo.color}20`, color: methodInfo.color }} 
      />
    ) : (method || 'N/A');
  };

  const getOrderTypeChip = (type) => {
    const typeInfo = ORDER_TYPES[type];
    return typeInfo ? (
      <Chip 
        icon={typeInfo.icon} 
        label={typeInfo.label} 
        size="small" 
        sx={{ bgcolor: `${typeInfo.color}20`, color: typeInfo.color }} 
      />
    ) : (type || 'N/A');
  };

  // Performance Metrics Component
  const PerformanceMetrics = () => {
    const stats = statistics || {};
    
    const metrics = [
      { label: 'Total Revenue', value: formatCurrency(stats.total_revenue || 0), icon: <AttachMoney />, color: '#2E7D32' },
      { label: 'Total Orders', value: stats.total_orders || 0, icon: <Receipt />, color: '#1976D2' },
      { label: 'Avg Order Value', value: formatCurrency(stats.average_order_value || 0), icon: <TrendingUp />, color: '#FF9800' },
      { label: 'Complimentary', value: stats.complimentary_count || 0, icon: <School />, color: '#9E9E9E' }
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <StatsCard>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Avatar sx={{ bgcolor: metric.color, width: 40, height: 40 }}>{metric.icon}</Avatar>
                </Box>
                <Typography variant="h6" fontWeight="bold">{metric.value}</Typography>
                <Typography variant="caption" color="textSecondary">{metric.label}</Typography>
              </CardContent>
            </StatsCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Payment Breakdown Component
  const PaymentBreakdown = () => {
    const stats = statistics || {};
    
    const methods = [
      { method: 'Cash', label: 'Cash', icon: <AttachMoney />, color: '#4CAF50', amount: stats.cash_total || 0 },
      { method: 'POS', label: 'POS Terminal', icon: <CreditCard />, color: '#2196F3', amount: stats.pos_total || 0 },
      { method: 'Transfer', label: 'Bank Transfer', icon: <Payments />, color: '#9C27B0', amount: stats.transfer_total || 0 },
      { method: 'Complimentary', label: 'Complimentary', icon: <School />, color: '#9E9E9E', amount: stats.complimentary_amount || 0, count: stats.complimentary_count || 0 }
    ].filter(m => m.amount > 0 || m.method === 'Cash');
    
    if (methods.length === 0 || methods.every(m => m.amount === 0)) {
      methods.push({ method: 'No Payments', label: 'No Payments', icon: <AttachMoney />, color: '#9E9E9E', amount: 0, percentage: 0 });
    }
    
    const totalRevenue = stats.total_revenue || 0;
    
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          💳 Payment Method Breakdown
        </Typography>
        <Grid container spacing={2}>
          {methods.map(method => (
            <Grid item xs={12} sm={6} md={3} key={method.method}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: `1px solid ${method.color}30`, bgcolor: `${method.color}08` }}>
                <Avatar sx={{ bgcolor: method.color, width: 40, height: 40 }}>{method.icon}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="textSecondary">{method.label}</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(method.amount)}</Typography>
                  {method.count !== undefined && method.count > 0 && (
                    <Typography variant="caption" sx={{ color: method.color }}>{method.count} order(s)</Typography>
                  )}
                  {totalRevenue > 0 && method.amount > 0 && (
                    <Typography variant="caption" sx={{ color: method.color }}>{((method.amount / totalRevenue) * 100).toFixed(1)}%</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  // Order Type Breakdown Component
  const OrderTypeBreakdown = () => {
    const stats = statistics || {};
    const orderTypeStats = stats.order_type_breakdown || {};
    
    if (Object.keys(orderTypeStats).length === 0) {
      return null;
    }
    
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          📊 Order Type Breakdown
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(orderTypeStats).map(([type, data]) => {
            const typeInfo = ORDER_TYPES[type] || { label: type, icon: <Restaurant />, color: '#757575' };
            return (
              <Grid item xs={12} sm={6} md={3} key={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, border: `1px solid ${typeInfo.color}30`, bgcolor: `${typeInfo.color}08` }}>
                  <Avatar sx={{ bgcolor: typeInfo.color, width: 40, height: 40 }}>{typeInfo.icon}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="textSecondary">{typeInfo.label}</Typography>
                    <Typography variant="h6" fontWeight="bold">{data.count} orders</Typography>
                    <Typography variant="caption" sx={{ color: typeInfo.color }}>{formatCurrency(data.revenue)}</Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  // Enhanced Transactions Table Component with Expandable Items
  const TransactionsTable = () => {
    const filteredPayments = getFilteredPayments();
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (filteredPayments.length === 0) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">No transactions found</Typography>
          <Typography variant="body2" color="textSecondary">
            No transactions found for {formatDate(selectedDate)}
          </Typography>
        </Paper>
      );
    }
    
    const totalItemsSold = filteredPayments.reduce((sum, p) => sum + (p.items?.reduce((itemSum, item) => itemSum + (item.quantity || item.qty || 1), 0) || 0), 0);
    
    return (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">📋 Transactions</Typography>
              <Typography variant="caption" color="textSecondary">
                {filteredPayments.length} orders • {totalItemsSold} items sold
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search by order #, guest..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}><Clear fontSize="small" /></IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ width: 280 }}
            />
          </Box>
        </Box>
        
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Order #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Table/Room</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Items</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment) => (
                <React.Fragment key={payment.payment_id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{payment.order_number || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>{getOrderTypeChip(payment.order_type)}</TableCell>
                    <TableCell>
                      {payment.table_number ? `Table ${payment.table_number}` : 
                       payment.room_number ? `Room ${payment.room_number}` : 'Walk-in'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={payment.guest_phone || ''}>
                        <span>{payment.guest_name || 'Guest'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={payment.items?.length || 0} color="primary" max={99}>
                        <Fastfood fontSize="small" />
                      </Badge>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" sx={{ color: payment.payment_method === 'Complimentary' ? '#9E9E9E' : '#2E7D32' }}>
                        {formatCurrency(payment.paid_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getPaymentMethodChip(payment.payment_method)}</TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(payment.payment_date)}>
                        <span>{formatTime(payment.payment_date)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        onClick={() => toggleOrderExpansion(payment.payment_id)}
                        sx={{ transform: expandedOrders[payment.payment_id] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                      >
                        <ExpandMore fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded items row */}
                  {expandedOrders[payment.payment_id] && payment.items && payment.items.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ bgcolor: '#fafafa', p: 2 }}>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Order Items:
                          </Typography>
                          <List dense disablePadding>
                            {payment.items.map((item, idx) => {
                              const quantity = item.quantity || item.qty || 1;
                              const itemName = item.item_name || item.name || item.product_name || 'Item';
                              const itemPrice = item.price || item.unit_price || 0;
                              const itemTotal = quantity * itemPrice;
                              
                              return (
                                <ListItem key={idx} disablePadding sx={{ pl: 2, py: 0.5 }}>
                                  <ListItemAvatar sx={{ minWidth: 30 }}>
                                    <Fastfood fontSize="small" sx={{ color: '#757575' }} />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography variant="body2">
                                          <strong>{quantity}x</strong> {itemName}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ mr: 2 }}>
                                          {formatCurrency(itemTotal)}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      item.modifiers && item.modifiers.length > 0 ? (
                                        <Typography variant="caption" color="textSecondary">
                                          Modifiers: {item.modifiers.map(m => m.name || m).join(', ')}
                                        </Typography>
                                      ) : null
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                          {payment.items.length === 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                              No item details available
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          ml: isMobile ? 0 : sidebarOpen ? '240px' : '65px',
          transition: 'margin 0.2s ease-in-out, width 0.2s ease-in-out',
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%'
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />
        
        <Box sx={{ mt: 2, mb: 3 }}>
          <ReportHeader elevation={0}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    📋 Staff Daily Sales Report
                  </Typography>
                  <Typography variant="subtitle1">
                    {staff ? `${staff.first_name || ''} ${staff.other_names || ''}`.trim() : getCurrentStaffName()} • {staff?.staff_id || getCurrentStaffId()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<Today />} label={format(new Date(selectedDate), 'EEEE, dd MMMM yyyy')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    {shift && (
                      <Chip icon={<EventNote />} label={`Shift: ${shift.shift_start_time || '08:00'} - ${shift.shift_end_time || '07:30'}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <IconButton onClick={goToPreviousDay} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                    <NavigateBefore />
                  </IconButton>
                  <TextField
                    type="date"
                    size="small"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: 'white' } }}
                  />
                  <IconButton onClick={goToNextDay} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                    <NavigateNext />
                  </IconButton>
                  <Button variant="contained" size="small" onClick={goToToday} sx={{ bgcolor: 'white', color: '#1a237e' }}>
                    Today
                  </Button>
                  <Tooltip title="Refresh">
                    <IconButton onClick={refreshData} disabled={refreshing} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}>
                      {refreshing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Box sx={{ position: 'absolute', right: -30, bottom: -30, opacity: 0.08 }}>
              <Receipt sx={{ fontSize: 200 }} />
            </Box>
          </ReportHeader>
        </Box>
        
        {/* Export Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            startIcon={<Print />} 
            onClick={printThermalReport} 
            sx={{ 
              borderRadius: 2, 
              bgcolor: '#2E7D32', 
              '&:hover': { bgcolor: '#1B5E20' },
              fontWeight: 'bold'
            }}
          >
            Print Thermal Report
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportToPDF} sx={{ borderRadius: 2 }}>
            Export PDF
          </Button>
          <Button variant="outlined" startIcon={<Description />} onClick={exportToExcel} sx={{ borderRadius: 2 }}>
            Export Excel
          </Button>
        </Box>
        
        {/* Main Content */}
        <Box sx={{ mt: 2 }}>
          <PerformanceMetrics />
          <PaymentBreakdown />
          <OrderTypeBreakdown />
          <TransactionsTable />
        </Box>
      </Box>
      
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffDailyReport;